import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Alert,
  AlertTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SchoolIcon from "@mui/icons-material/School";

function InstructionsPopup({
  currentAssessmentId,
  setInstructionsPopup,
  assessmentEnded,
}) {
  const [disableYesButton, setDisableYesButton] = useState(false);

  const checkAssessmentStatus = () => {
    if (assessmentEnded) {
      toast.error("Assessment Ended!!");
    } else {
      handleOpenAssessment(currentAssessmentId);
      setInstructionsPopup(false);
    }
  };

  const handleOpenAssessment = (currentAssessmentId) => {
    setDisableYesButton(true);
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const newWindow = window.open(
      `/Assessment/pending/questions/${currentAssessmentId}`,
      "_blank",
      `width=${screenWidth}px,height=${screenHeight}px`,
    );
    if (newWindow) {
      newWindow.addEventListener("beforeunload", () => {
        window?.location?.reload();
      });
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => setInstructionsPopup(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          padding: "20px 24px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              backgroundColor: "#e0f2fe",
              borderRadius: "12px",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SchoolIcon sx={{ color: "#0284c7", fontSize: 28 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            Assessment Confirmation
          </Typography>
        </Box>
        <IconButton
          onClick={() => setInstructionsPopup(false)}
          size="small"
          sx={{
            color: "#64748b",
            "&:hover": { backgroundColor: "#f1f5f9" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ padding: "24px", backgroundColor: "#ffffff" }}>
        {/* Warning Alert */}
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 3,
            borderRadius: "12px",
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            "& .MuiAlert-icon": { color: "#d97706" },
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, color: "#92400e" }}>
            PLEASE READ BELOW CAREFULLY
          </AlertTitle>
          <Typography variant="body2" sx={{ color: "#78350f" }}>
            These instructions are important for a smooth assessment experience.
          </Typography>
        </Alert>

        {/* Instructions List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[
            {
              icon: "1",
              text: "After Starting the Assessment, Please DON'T Close Assessment Window.",
            },
            {
              icon: "2",
              text: "Assessment will automatically Close on Submission or Timer Expiry.",
            },
            {
              icon: "3",
              text: "On Timer Expiry, Assessment will automatically Submit in the existing state.",
            },
            {
              icon: "4",
              text: "You Won't Be Able To Enter Assessment Screen Again After Exit.",
            },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                padding: "12px 16px",
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  borderColor: "#cbd5e1",
                },
              }}
            >
              <Box
                sx={{
                  minWidth: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  backgroundColor: "#0284c7",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  marginTop: "1px",
                }}
              >
                {item.icon}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#334155",
                  lineHeight: 1.7,
                  fontSize: "0.9rem",
                }}
              >
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 3, borderColor: "#e2e8f0" }} />

        {/* Confirmation Question */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #bae6fd",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#0c4a6e",
              textAlign: "center",
            }}
          >
            Do you want to Start Assessment Now?
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          padding: "16px 24px 24px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          gap: 2,
          justifyContent: "center",
        }}
      >
        <Button
          onClick={() => setInstructionsPopup(false)}
          variant="outlined"
          sx={{
            borderRadius: "10px",
            padding: "10px 32px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          No
        </Button>
        <Button
          onClick={checkAssessmentStatus}
          disabled={disableYesButton}
          variant="contained"
          sx={{
            borderRadius: "10px",
            padding: "10px 32px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            backgroundColor: "#0284c7",
            boxShadow: "0 4px 12px rgba(2, 132, 199, 0.3)",
            "&:hover": {
              backgroundColor: "#0369a1",
              boxShadow: "0 6px 16px rgba(2, 132, 199, 0.4)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
              color: "#94a3b8",
              boxShadow: "none",
            },
          }}
        >
          Yes, Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InstructionsPopup;
